import { StockDto } from '../../../types/stocks';

export interface PositionAnalysis {
  symbol: string;
  stockPublicId: string;
  totalShares: number;
  totalInvested: number;
  averagePrice: number;
  currentValue: number;
  gainLoss: number;
  gainLossPercentage: number;
  dividends: number;
  // EUR-based metrics
  totalInvestedEur: number;
  averagePriceEur: number;
  currentValueEur: number;
  gainLossNoDivEur: number;
  gainLossWithDivEur: number;
  gainLossNoDivEurPct: number;
  gainLossWithDivEurPct: number;
}

function analyzeStock(stock: StockDto): PositionAnalysis {
  // Compute remaining shares and remaining cost basis after sales using salePurchases breakdown
  const purchases = Array.isArray(stock.purchases) ? stock.purchases : [];
  const sales = Array.isArray(stock.sales) ? stock.sales : [];

  const soldByPurchase = new Map<string, number>();
  sales.forEach(sale => {
    const sp = Array.isArray(sale.salePurchases) ? sale.salePurchases : [];
    sp.forEach(link => {
      const pid = link.purchasePublicId;
      const qty = Number(link.quantitySold || 0);
      if (pid) soldByPurchase.set(pid, (soldByPurchase.get(pid) || 0) + qty);
    });
  });

  let remainingShares = 0;
  let remainingCostBasis = 0;
  let remainingCostBasisEur = 0;
  purchases.forEach(p => {
    const soldQty = soldByPurchase.get(p.publicId) || 0;
    const remQty = Math.max(0, Number(p.quantity || 0) - Number(soldQty));
    remainingShares += remQty;
    remainingCostBasis += remQty * Number(p.pricePerShare || 0);
    const pCambio = (typeof p.cambio === 'number' ? p.cambio : undefined);
    const pricePerShareEur = (typeof p.pricePerShareEUR === 'number' && p.pricePerShareEUR != null)
      ? Number(p.pricePerShareEUR)
      : Number(p.pricePerShare || 0) * Number(pCambio ?? (stock.currentCambio ?? 1));
    remainingCostBasisEur += remQty * pricePerShareEur;
  });

  const averagePriceRaw = remainingShares > 0 ? remainingCostBasis / remainingShares : 0;
  const averagePrice = Number(averagePriceRaw.toFixed(4));
  const currentValue = remainingShares * (stock.currentPrice || 0);
  const cambio = Number(stock.currentCambio ?? 1);
  const currentValueEur = remainingShares * (stock.currentPrice || 0) * cambio;
  const gainLoss = currentValue - remainingCostBasis;
  const gainLossPercentage = remainingCostBasis > 0 ? (gainLoss / remainingCostBasis) * 100 : 0;
  const dividends = (Array.isArray(stock.dividends) ? stock.dividends : []).reduce((sum, d) => sum + Number(d.amount || 0), 0);
  const averagePriceEurRaw = remainingShares > 0 ? (remainingCostBasisEur / remainingShares) : 0;
  const gainLossNoDivEur = currentValueEur - remainingCostBasisEur;
  const gainLossWithDivEur = gainLossNoDivEur + dividends;
  const gainLossNoDivEurPct = remainingCostBasisEur > 0 ? (gainLossNoDivEur / remainingCostBasisEur) * 100 : 0;
  const gainLossWithDivEurPct = remainingCostBasisEur > 0 ? (gainLossWithDivEur / remainingCostBasisEur) * 100 : 0;
  return {
    symbol: stock.symbol,
    stockPublicId: stock.publicId,
    totalShares: remainingShares,
    totalInvested: Number(remainingCostBasis.toFixed(2)),
    averagePrice,
    currentValue: Number(currentValue.toFixed(2)),
    gainLoss: Number(gainLoss.toFixed(2)),
    gainLossPercentage,
    dividends: Number(dividends.toFixed(2)),
    totalInvestedEur: Number(remainingCostBasisEur.toFixed(2)),
    averagePriceEur: Number(averagePriceEurRaw.toFixed(4)),
    currentValueEur: Number(currentValueEur.toFixed(2)),
    gainLossNoDivEur: Number(gainLossNoDivEur.toFixed(2)),
    gainLossWithDivEur: Number(gainLossWithDivEur.toFixed(2)),
    gainLossNoDivEurPct: Number(gainLossNoDivEurPct.toFixed(2)),
    gainLossWithDivEurPct: Number(gainLossWithDivEurPct.toFixed(2))
  };
}

export function batchAnalyze(stocks: StockDto[]): PositionAnalysis[] {
  return stocks.map(analyzeStock);
}

// Rate-based helpers removed as part of revert
